import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, Plus, ShieldCheck, Stethoscope, Sparkles } from 'lucide-react';
import { formatarNomeExameAmigavel } from '../../../utils/exames.utils';

export interface ExameItemCatalog {
  id: number;
  nome: string;
  codigo?: string;
  laboratorial?: boolean;
}

interface ExamesPontuaisSearchInputProps {
  selectedExamIds: number[];
  protocoloExamIds?: number[];
  pontuaisExamIds?: number[];
  catalogExames: ExameItemCatalog[];
  onAddExam: (exameId: number) => void;
  onRemoveExam: (exameId: number) => void;
  disabled?: boolean;
}

export default function ExamesPontuaisSearchInput({
  selectedExamIds,
  protocoloExamIds = [],
  pontuaisExamIds = [],
  catalogExames,
  onAddExam,
  onRemoveExam,
  disabled = false,
}: ExamesPontuaisSearchInputProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Normalização para busca
  const normalize = (str?: string) =>
    (str || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  // Filtragem leve de até 8 sugestões
  const filteredSuggestions = useMemo(() => {
    const term = normalize(searchTerm);
    if (!term || term.length < 2) return [];

    const selectedSet = new Set(selectedExamIds);

    return catalogExames
      .filter((ex) => {
        if (selectedSet.has(ex.id)) return false;
        const nomeNorm = normalize(ex.nome);
        const codNorm = normalize(ex.codigo);
        return nomeNorm.includes(term) || codNorm.includes(term);
      })
      .slice(0, 8);
  }, [searchTerm, catalogExames, selectedExamIds]);

  const handleSelectExam = (exameId: number) => {
    onAddExam(exameId);
    setSearchTerm('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  // Mapa para resolução rápida de nomes de exames selecionados
  const catalogMap = useMemo(() => {
    const map = new Map<number, ExameItemCatalog>();
    catalogExames.forEach((e) => map.set(e.id, e));
    return map;
  }, [catalogExames]);

  const pcmsoSet = useMemo(() => new Set(protocoloExamIds), [protocoloExamIds]);
  const pontuaisSet = useMemo(() => new Set(pontuaisExamIds), [pontuaisExamIds]);

  return (
    <div className="space-y-2.5" ref={containerRef}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
        <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
          <Stethoscope className="w-3.5 h-3.5 text-emerald-600" />
          <span>Exames do Agendamento ({selectedExamIds.length + 1})</span>
        </label>
        <span className="text-[11px] text-gray-500 font-normal">
          ASO Ocupacional obrigatório + Protocolo PGR/PCMSO + Exames Pontuais
        </span>
      </div>

      {/* Tags / Chips dos Exames Selecionados */}
      <div className="flex flex-wrap gap-1.5 items-center">
        {/* Exame Clínico (ASO) sempre presente */}
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-300 shadow-2xs">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
          <span>Avaliação Clínica (ASO) • Obrigatório</span>
        </span>

        {/* Exames do Protocolo PCMSO */}
        {selectedExamIds.map((examId) => {
          if (examId === 28) return null; // ID reservado do ASO padrão
          const isPontual = pontuaisSet.has(examId) || (!pcmsoSet.has(examId) && pcmsoSet.size > 0);
          const examObj = catalogMap.get(examId);
          const rawName = examObj?.nome || `Exame #${examId}`;
          const friendlyName = formatarNomeExameAmigavel(rawName);

          if (isPontual) {
            return (
              <span
                key={examId}
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg text-[11px] font-bold bg-indigo-50 text-indigo-900 border border-indigo-200 shadow-2xs animate-in fade-in"
              >
                <Sparkles className="w-3 h-3 text-indigo-600 shrink-0" />
                <span className="truncate max-w-[220px]" title={friendlyName}>
                  {friendlyName}
                </span>
                <span className="text-[9px] uppercase px-1.5 py-0.2 rounded bg-indigo-200/70 text-indigo-800 font-extrabold">
                  Pontual
                </span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onRemoveExam(examId)}
                  className="p-0.5 hover:bg-indigo-200/80 rounded text-indigo-600 hover:text-indigo-900 transition-colors cursor-pointer"
                  title={`Remover exame pontual ${friendlyName}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            );
          }

          return (
            <span
              key={examId}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-2xs"
            >
              <ShieldCheck className="w-3 h-3 text-emerald-600 shrink-0" />
              <span className="truncate max-w-[220px]" title={friendlyName}>
                {friendlyName}
              </span>
              <span className="text-[9px] uppercase px-1.5 py-0.2 rounded bg-emerald-200/60 text-emerald-800 font-bold">
                PCMSO
              </span>
            </span>
          );
        })}
      </div>

      {/* Input Search de Inclusão Pontual */}
      {!disabled && (
        <div className="relative pt-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-gray-400">
              <Search className="w-3.5 h-3.5" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onFocus={() => {
                if (searchTerm.trim().length >= 2) setIsOpen(true);
              }}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTerm(val);
                setIsOpen(val.trim().length >= 2);
              }}
              placeholder="Pesquise para adicionar exame pontual extra (ex: Audiometria, Acuidade, Raio-X, Hemograma...)"
              className="w-full pl-8 pr-8 py-1.5 text-xs bg-white border border-gray-300 hover:border-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 rounded-xl transition-all placeholder:text-gray-400 shadow-2xs"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setIsOpen(false);
                }}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Dropdown Flutuante de Resultados */}
          {isOpen && (
            <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden max-h-56 overflow-y-auto">
              {filteredSuggestions.length === 0 ? (
                <div className="p-3 text-center text-xs text-gray-500">
                  Nenhum exame disponível encontrado com "{searchTerm}".
                </div>
              ) : (
                <div className="py-1">
                  <div className="px-3 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50/70 border-b border-gray-100 flex items-center justify-between">
                    <span>Clique para incluir no agendamento:</span>
                    <span>{filteredSuggestions.length} resultado(s)</span>
                  </div>
                  {filteredSuggestions.map((ex) => (
                    <button
                      key={ex.id}
                      type="button"
                      onClick={() => handleSelectExam(ex.id)}
                      className="w-full text-left px-3.5 py-2 hover:bg-indigo-50/80 transition-colors flex items-center justify-between group border-b border-gray-50 last:border-0"
                    >
                      <div className="flex items-center gap-2 truncate pr-2">
                        <Plus className="w-3.5 h-3.5 text-indigo-600 group-hover:scale-110 transition-transform shrink-0" />
                        <span className="text-xs font-semibold text-gray-900 group-hover:text-indigo-900 truncate">
                          {formatarNomeExameAmigavel(ex.nome)}
                        </span>
                        {ex.laboratorial && (
                          <span className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.2 rounded font-medium shrink-0">
                            Laboratorial
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 group-hover:bg-indigo-600 group-hover:text-white px-2 py-0.5 rounded-lg transition-colors shrink-0">
                        + Incluir
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
